import { useState, useEffect } from "react";
import {
  Package,
  Zap,
  Heart,
  Star,
  TrendingUp,
  Gift,
  ShoppingBag,
  DollarSign,
  Users,
  Award,
  Percent,
  Layers,
} from "lucide-react";

export default function AffiliatePreloader() {
  const [progress, setProgress] = useState(0);
  const [iconIndex, setIconIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [showLogo, setShowLogo] = useState(false);

  // Array de íconos para la animación principal
  const icons = [
    {
      icon: ShoppingBag,
      message: "Preparando productos premium...",
      color: "text-pink-400",
    },
    {
      icon: DollarSign,
      message: "Calculando comisiones...",
      color: "text-green-400",
    },
    {
      icon: Zap,
      message: "Optimizando ofertas flash...",
      color: "text-yellow-400",
    },
    {
      icon: Users,
      message: "Conectando con afiliados top...",
      color: "text-blue-400",
    },
    {
      icon: Award,
      message: "Seleccionando promociones VIP...",
      color: "text-purple-400",
    },
    {
      icon: Percent,
      message: "Maximizando tus ganancias...",
      color: "text-red-400",
    },
  ];

  // Íconos flotantes adicionales para el fondo
  const backgroundIcons = [Package, Heart, Star, TrendingUp, Gift, Layers];

  useEffect(() => {
    // Mostrar logo con un pequeño delay
    setTimeout(() => {
      setShowLogo(true);
    }, 300);

    // Simulación de progreso de carga con aceleración no lineal
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        // Hacer que el progreso vaya más rápido al principio y más lento al final
        let increment = 0.7;
        if (prevProgress < 30) increment = 1.2;
        if (prevProgress > 80) increment = 0.4;

        if (prevProgress >= 100) {
          clearInterval(interval);

          // Ocultar el preloader después de completarse con una transición
          setTimeout(() => {
            setVisible(false);
          }, 800);

          return 100;
        }
        return Math.min(prevProgress + increment, 100);
      });
    }, 40);

    // Cambia el ícono cada cierto tiempo
    const iconInterval = setInterval(() => {
      setIconIndex((prevIndex) => (prevIndex + 1) % icons.length);
    }, 1800);

    return () => {
      clearInterval(interval);
      clearInterval(iconInterval);
    };
  }, []);

  if (!visible) return null;

  const CurrentIcon = icons[iconIndex].icon;
  const currentMessage = icons[iconIndex].message;
  const currentColor = icons[iconIndex].color;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-purple-950 to-blue-900 flex flex-col items-center justify-center text-white z-50 overflow-hidden">
      {/* Partículas de fondo */}
      <div className="absolute inset-0 overflow-hidden opacity-40">
        {Array.from({ length: 20 }).map((_, i) => {
          const Icon = backgroundIcons[i % backgroundIcons.length];
          const size = 10 + Math.random() * 30;
          const left = Math.random() * 100;
          const top = Math.random() * 100;
          const duration = 15 + Math.random() * 30;
          const delay = Math.random() * -20;
          const opacity = 0.1 + Math.random() * 0.3;

          return (
            <div
              key={i}
              className="absolute text-white"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                opacity,
                transform: "scale(0)",
                animation: `pulse ${duration}s ${delay}s infinite ease-in-out`,
              }}
            >
              <Icon size={size} />
            </div>
          );
        })}
      </div>

      {/* Efecto de círculos concéntricos */}
      <div className="absolute inset-0 flex items-center justify-center">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-white/10"
            style={{
              width: `${(i + 1) * 200}px`,
              height: `${(i + 1) * 200}px`,
              animationDelay: `${i * 0.5}s`,
              animation: "ripple 4s infinite ease-out",
            }}
          />
        ))}
      </div>

      {/* Contenedor principal con efecto de aparición */}
      <div
        className={`flex flex-col items-center max-w-md text-center transition-all duration-1000 transform ${
          showLogo ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Logo con animación de rotación 3D */}
        <div className="mb-8 relative perspective-1000">
          <div
            className={`absolute -inset-8 rounded-full bg-gradient-to-r from-purple-500/30 to-blue-500/30 blur-xl animate-pulse ${currentColor}`}
          ></div>
          <div
            className="relative bg-white/5 backdrop-blur-sm p-10 rounded-full border border-white/10 shadow-2xl transform-gpu transition-all duration-500"
            style={{ animation: "float 6s infinite ease-in-out" }}
          >
            <div
              className="transform-gpu"
              style={{ animation: "spin3d 8s infinite linear" }}
            >
              <CurrentIcon
                size={80}
                className={`${currentColor} drop-shadow-glow transition-colors duration-700`}
              />
            </div>
          </div>
        </div>

        {/* Título con efecto de resplandor */}
        <div className="relative">
          <h1 className="text-4xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
            Tu App de Afiliados
          </h1>
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-400/20 to-purple-500/20 blur-sm -z-10 rounded-lg"></div>
        </div>

        {/* Mensaje con animación de desvanecimiento */}
        <div className="h-8 mb-8">
          <p
            className={`text-lg ${currentColor} transition-all duration-500 animate-fadeIn`}
            key={iconIndex}
          >
            {currentMessage}
          </p>
        </div>

        {/* Barra de progreso mejorada */}
        <div className="w-80 h-2 bg-white/10 rounded-full mb-4 overflow-hidden backdrop-blur-sm border border-white/10">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full transition-all duration-300 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            {/* Efecto brillante en la barra de progreso */}
            <div
              className="absolute top-0 right-0 h-full w-8 bg-gradient-to-r from-transparent to-white/50"
              style={{ animation: "shimmer 1.5s infinite" }}
            />
          </div>
        </div>

        {/* Porcentaje con número grande */}
        <div className="flex items-baseline">
          <p className="text-2xl font-bold text-white">
            {Math.round(progress)}
          </p>
          <p className="text-sm text-white/70">%</p>
        </div>

        {/* Texto de carga con puntos animados */}
        <div className="mt-2 text-white/60 flex items-center">
          <span>Cargando</span>
          <span className="inline-flex w-10 justify-start ml-1">
            <span className="animate-dot" style={{ animationDelay: "0s" }}>
              .
            </span>
            <span className="animate-dot" style={{ animationDelay: "0.2s" }}>
              .
            </span>
            <span className="animate-dot" style={{ animationDelay: "0.4s" }}>
              .
            </span>
          </span>
        </div>
      </div>

      {/* Estilos CSS para las animaciones */}
      <style jsx>{`
        @keyframes ripple {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            opacity: 0.2;
          }
          100% {
            transform: scale(1.1);
            opacity: 0;
          }
        }

        @keyframes pulse {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0);
            opacity: 0;
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes spin3d {
          0% {
            transform: rotateY(0deg);
          }
          100% {
            transform: rotateY(360deg);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .drop-shadow-glow {
          filter: drop-shadow(0 0 8px currentColor);
        }

        .perspective-1000 {
          perspective: 1000px;
        }

        .animate-dot {
          animation: dotPulse 1.5s infinite;
          opacity: 0;
        }

        @keyframes dotPulse {
          0% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
